import { updateTodo } from '../actions/todoActions';
import getSelectedFolderView from '../selectors/getSelectedFolderView';
import selectTodoById from '../selectors/selectTodoById';
import getOrderTimeForDrop from '../utils/getOrderTimeForDrop';
import { Todo, extractTodosFromMailListItemDragData } from 'owa-todo-types';
import { orchestrator } from 'satcheljs';
import {
    dropMailItems,
    reorderTodos,
    dropTodoOnRecentlyCompletedTasks,
    moveTaskToTopOfList,
    moveTaskUpTheList,
    moveTaskDownTheList,
    moveTaskToEndOfList,
    dropTodoOnEndOfListSpacer,
} from '../actions/dropActions';
import {
    updatePositions,
    moveFromCompletedToActiveList,
    moveFromActiveToCompletedList,
} from '../actions/todoFolderViewActions';
import { createTasks } from './createTasks';

export const dropMailItemsOrchestrator = orchestrator(dropMailItems, actionMessage => {
    const partialTodos = extractTodosFromMailListItemDragData(actionMessage.dragData);
    if (partialTodos.length > 0) {
        createTasks(partialTodos, actionMessage.folderId);
    }
});

export const reorderTodosOrchestrator = orchestrator(reorderTodos, async actionMessage => {
    handleReorderTask(actionMessage.draggedTargetId, actionMessage.droppedTargetId);
});

function handleReorderTask(draggedTargetId: string, droppedTargetId: string) {
    // If dropping over itself, there's nothing to do. Early exit.
    if (draggedTargetId === droppedTargetId) {
        return;
    }

    const draggedTodo = getTodoById(draggedTargetId);
    if (!draggedTodo) {
        // The dragged item may be in the completed list, so let's try that.
        attemptMoveFromCompletedToActive(draggedTargetId, droppedTargetId);
    } else {
        attemptReorderWithinActiveTasks(draggedTargetId, droppedTargetId);
    }
}

export const dropTodoOnEndOfListSpacerOrchestrator = orchestrator(
    dropTodoOnEndOfListSpacer,
    async actionMessage => {
        const itemId = actionMessage.itemId;

        // setting droppedTargetId to null reorders item to end of the list
        handleReorderTask(itemId, null /* droppedTargetId */);
    }
);

function attemptReorderWithinActiveTasks(draggedTargetId: string, droppedTargetId: string) {
    const draggedTodo = getTodoById(draggedTargetId);
    if (!draggedTodo) {
        return;
    }

    const droppedOverTodo = getTodoById(droppedTargetId);
    const dropIndex = droppedOverTodo
        ? droppedOverTodo.index
        : getSelectedFolderView().activeTaskIds.length;

    // If dragged item is right before droppedOver item, then we can ignore this drop.
    // The dragged item is already in the right place.
    if (draggedTodo.index === dropIndex - 1) {
        return;
    }

    reorderByIndexWithinActiveTasks(draggedTargetId, draggedTodo.index, dropIndex);
}

function attemptMoveFromCompletedToActive(draggedTargetId: string, droppedTargetId: string) {
    // Is this item in the completed items list? If not, ignore this operation
    const index = getSelectedFolderView().recentlyCompletedTaskIds.indexOf(draggedTargetId);
    if (index == -1) {
        return;
    }

    // Update items to have a renew time between the target id and the task preceeding it.
    const droppedOverTodo = getTodoById(droppedTargetId);
    const dropIndex = droppedOverTodo
        ? droppedOverTodo.index
        : getSelectedFolderView().activeTaskIds.length;
    const newOrderTimes = getOrderTimeForDrop(dropIndex);

    // Local lies first
    moveFromCompletedToActiveList(draggedTargetId, dropIndex);

    // Update the todo
    // This item is in completed items, so we should move it into incomplete state
    updateTodo(draggedTargetId, { OrderDateTime: newOrderTimes[0], Status: 'InProgress' });
}

function getTodoById(itemId: string): { index: number; item: Todo } {
    const selectedFolder = getSelectedFolderView();

    const index = selectedFolder.activeTaskIds.indexOf(itemId);
    const todo = selectTodoById(itemId);

    if (index >= 0 && index < selectedFolder.activeTaskIds.length && todo) {
        return { index: index, item: todo };
    } else {
        return null;
    }
}

export const dropTodoOnRecentlyCompletedTasksOrchestrator = orchestrator(
    dropTodoOnRecentlyCompletedTasks,
    actionMessage => {
        moveFromActiveToCompletedList(actionMessage.itemId);
        updateTodo(actionMessage.itemId, { Status: 'Completed' });
    }
);

export const moveTaskToTopOfListOrchestrator = orchestrator(
    moveTaskToTopOfList,
    async actionMessage => {
        const { itemId } = actionMessage;
        const todo = getTodoById(itemId);
        if (!todo || todo.index === 0) {
            return;
        }

        reorderByIndexWithinActiveTasks(itemId, todo.index, 0);
    }
);

export const moveTaskUpTheListOrchestrator = orchestrator(
    moveTaskUpTheList,
    async actionMessage => {
        const { itemId } = actionMessage;
        const todo = getTodoById(itemId);
        if (!todo || todo.index === 0) {
            return;
        }

        reorderByIndexWithinActiveTasks(itemId, todo.index, todo.index - 1);
    }
);

export const moveTaskDownTheListOrchestrator = orchestrator(
    moveTaskDownTheList,
    async actionMessage => {
        const { itemId } = actionMessage;
        const todo = getTodoById(itemId);
        if (!todo) {
            return;
        }

        const selectedFolder = getSelectedFolderView();
        const countOfTasks = selectedFolder.activeTaskIds.length;

        if (todo.index == countOfTasks - 1) {
            // This is the last item in the list already.
            return;
        }

        // Move ahead of item at index + 2, which will place it below index + 1.
        reorderByIndexWithinActiveTasks(itemId, todo.index, todo.index + 2);
    }
);

export const moveTaskToEndOfListOrchestrator = orchestrator(
    moveTaskToEndOfList,
    async actionMessage => {
        const { itemId } = actionMessage;
        const todo = getTodoById(itemId);
        if (!todo) {
            return;
        }

        const selectedFolder = getSelectedFolderView();
        const countOfTasks = selectedFolder.activeTaskIds.length;

        if (todo.index == countOfTasks - 1) {
            // This is the last item in the list already.
            return;
        }

        reorderByIndexWithinActiveTasks(itemId, todo.index, countOfTasks);
    }
);

function reorderByIndexWithinActiveTasks(
    draggedTargetId: string,
    dragIndex: number,
    dropIndex: number
) {
    // Update items to have a renew time between the target id and the task preceeding it.
    const newOrderTimes = getOrderTimeForDrop(dropIndex);

    // Local lie
    // Only apply local lie for position update here. updateTodo action will update the OrderDateTime on the todo.
    updatePositions(dragIndex, dropIndex);

    // Update the todo
    updateTodo(draggedTargetId, { OrderDateTime: newOrderTimes[0] });
}
